import { makeAutoObservable, runInAction } from "mobx";
import { ImportRepository, ImportBatch, ImportPage, ImportEntry } from "./import_repository";

export class ImportStore {
  history: ImportBatch[] = [];
  data?: ImportPage;
  busy = false;
  saving = false;
  error = "";
  notice = "";
  progress = { done: 0, total: 0 };
  cancelRequested = false;
  constructor(public repository = new ImportRepository()) {
    makeAutoObservable(this, { repository: false });
  }
  async loadHistory() {
    const result = await this.repository.history();
    runInAction(() => {
      if (result.isFailure()) this.error = result.error.message;
      else this.history = result.getOrThrow();
    });
  }
  async load(id: string, page = 0) {
    this.busy = true;
    this.error = "";
    try {
      const result = await this.repository.page(id, page);
      if (result.isFailure()) throw result.error;
      runInAction(() => { this.data = result.getOrThrow(); });
    } catch (error) {
      runInAction(() => { this.error = error instanceof Error ? error.message : String(error); });
    } finally { runInAction(() => { this.busy = false; }); }
  }
  async preview(files: File[]) {
    if (this.busy || this.saving) return;
    this.busy = true;
    this.error = "";
    this.notice = "";
    try {
      if (!files.length || files.length > 1000 || files.reduce((sum, file) => sum + file.size, 0) > 100 * 1024 * 1024) throw new Error("Выберите пакет до 100 МБ и 1000 заданий");
      const result = await this.repository.preview(files);
      if (result.isFailure()) throw result.error;
      return result.getOrThrow().id;
    } catch (error) {
      runInAction(() => { this.error = error instanceof Error ? error.message : String(error); });
    } finally { runInAction(() => { this.busy = false; }); }
  }
  async select(entry: ImportEntry, selected: boolean) {
    if (!this.data || this.busy || this.saving) return;
    this.busy = true;
    this.error = "";
    const result = await this.repository.update(this.data.batch._id, entry, selected);
    runInAction(() => { this.busy = false; });
    if (result.isFailure()) { runInAction(() => { this.error = result.error.message; }); return; }
    await this.load(this.data.batch._id, this.data.page);
  }
  cancel() { this.cancelRequested = true; }
  async commit() {
    if (!this.data || this.busy || this.saving) return;
    const { batch, page, readyIds } = this.data;
    this.saving = true;
    this.cancelRequested = false;
    this.error = "";
    this.notice = "";
    this.progress = { done: 0, total: readyIds.length };
    try {
      for (let offset = 0; offset < readyIds.length && !this.cancelRequested; offset += 25) {
        const result = await this.repository.commit(batch._id, readyIds.slice(offset, offset + 25));
        if (result.isFailure()) throw result.error;
        const results = result.getOrThrow().results;
        runInAction(() => { this.progress.done += results.length; });
        if (results.some(entry => entry.result === "error")) throw new Error("Часть записей не сохранена. Причины указаны в отчёте; можно повторить оставшиеся.");
      }
      runInAction(() => { this.notice = this.cancelRequested ? "Остановлено между порциями. Сохранённые черновики остаются в банке." : "Выбранные задания сохранены в черновики."; });
    } catch (error) {
      runInAction(() => { this.error = error instanceof Error ? error.message : String(error); });
    } finally {
      runInAction(() => { this.saving = false; });
      const error = this.error;
      await this.load(batch._id, page);
      runInAction(() => { this.error = error || this.error; });
    }
  }
}
